import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  BriefcaseIcon,
  MapPinIcon,
  CalendarIcon,
  TrashIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { Card, Button, Loading, Modal } from '../components/common';
import { getJobs, deleteJob, updateJob } from '../services/jobService';

const statusColors = {
  saved: 'bg-gray-100 text-gray-800',
  tailored: 'bg-blue-100 text-blue-800',
  applied: 'bg-green-100 text-green-800',
  interviewing: 'bg-purple-100 text-purple-800',
  rejected: 'bg-red-100 text-red-800',
  offer: 'bg-yellow-100 text-yellow-800',
  archived: 'bg-gray-200 text-gray-600'
};

const statusLabels = {
  saved: 'Saved',
  tailored: 'Tailored',
  applied: 'Applied',
  interviewing: 'Interviewing',
  rejected: 'Rejected',
  offer: 'Offer',
  archived: 'Archived'
};

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteModal, setDeleteModal] = useState({ open: false, job: null });
  const [addModal, setAddModal] = useState(false);
  const [newJob, setNewJob] = useState({ title: '', company: '', description: '', url: '' });
  const [addingJob, setAddingJob] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, [filter]);

  const fetchJobs = async () => {
    try {
      const response = await getJobs({ status: filter });
      if (response.success) {
        setJobs(response.data.jobs);
      }
    } catch (err) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await updateJob(jobId, { status: newStatus });
      setJobs(jobs.map(j =>
        j.id === jobId ? { ...j, status: newStatus } : j
      ));
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.job) return;

    try {
      await deleteJob(deleteModal.job.id);
      setJobs(jobs.filter(j => j.id !== deleteModal.job.id));
      toast.success('Job deleted');
    } catch (err) {
      toast.error('Failed to delete job');
    } finally {
      setDeleteModal({ open: false, job: null });
    }
  };

  const handleAddJob = async (e) => {
    e.preventDefault();
    if (!newJob.title || !newJob.company) {
      toast.error('Title and company are required');
      return;
    }

    setAddingJob(true);
    try {
      const { createJob } = await import('../services/jobService');
      const response = await createJob(newJob);
      if (response.success) {
        setJobs([response.data.job, ...jobs]);
        setAddModal(false);
        setNewJob({ title: '', company: '', description: '', url: '' });
        toast.success('Job added successfully');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add job');
    } finally {
      setAddingJob(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loading size="lg" text="Loading jobs..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
          <p className="mt-1 text-gray-600">
            {jobs.length} job{jobs.length !== 1 ? 's' : ''} in your queue
          </p>
        </div>
        <Button onClick={() => setAddModal(true)}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Job
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <FunnelIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
        {['all', 'saved', 'tailored', 'applied', 'interviewing', 'offer'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === status
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status === 'all' ? 'All' : statusLabels[status]}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {jobs.length === 0 && (
        <Card className="text-center py-12">
          <BriefcaseIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No jobs yet
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Capture jobs from LinkedIn using our extension, or paste a job description manually.
          </p>
          <Button onClick={() => setAddModal(true)}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Your First Job
          </Button>
        </Card>
      )}

      {/* Job list */}
      {jobs.length > 0 && (
        <div className="space-y-3">
          {jobs.map((job) => (
            <Card key={job.id} className="hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <Link to={`/jobs/${job.id}`} className="flex-1 min-w-0">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <BriefcaseIcon className="h-6 w-6 text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {job.title}
                      </h3>
                      <p className="text-gray-600">{job.company}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        {job.location && (
                          <span className="flex items-center gap-1">
                            <MapPinIcon className="h-4 w-4" />
                            {job.location}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="h-4 w-4" />
                          {formatDate(job.capturedAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>

                <div className="flex items-center gap-3 ml-4">
                  {/* Match score */}
                  {job.matchScore?.overall && (
                    <div className="text-center">
                      <div className={`text-lg font-bold ${
                        job.matchScore.overall >= 70 ? 'text-green-600' :
                        job.matchScore.overall >= 50 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {job.matchScore.overall}%
                      </div>
                      <div className="text-xs text-gray-500">Match</div>
                    </div>
                  )}

                  {/* Status dropdown */}
                  <select
                    value={job.status}
                    onChange={(e) => handleStatusChange(job.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border-0 cursor-pointer ${statusColors[job.status]}`}
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>

                  {/* Delete button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setDeleteModal({ open: true, job });
                    }}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add job modal */}
      <Modal
        isOpen={addModal}
        onClose={() => setAddModal(false)}
        title="Add Job Manually"
        size="lg"
      >
        <form onSubmit={handleAddJob} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title *
              </label>
              <input
                type="text"
                value={newJob.title}
                onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Software Engineer"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company *
              </label>
              <input
                type="text"
                value={newJob.company}
                onChange={(e) => setNewJob({ ...newJob, company: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Google"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job URL
            </label>
            <input
              type="url"
              value={newJob.url}
              onChange={(e) => setNewJob({ ...newJob, url: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Description
            </label>
            <textarea
              value={newJob.description}
              onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
              className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Paste the full job description here for AI analysis..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Paste the job description for AI to extract requirements
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setAddModal(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={addingJob}>
              Add Job
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, job: null })}
        title="Delete Job"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete &quot;{deleteModal.job?.title}&quot; at {deleteModal.job?.company}?
        </p>
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary"
            onClick={() => setDeleteModal({ open: false, job: null })}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default JobsPage;
